// src/components/TenantPicker.tsx - March 15, 2026
// Shown after login when a user has access to multiple tenants.
// Single-tenant users skip this screen automatically (handled in App.tsx).

import { Building2, ChevronRight } from 'lucide-react';

export interface TenantInfo {
  slug: string;
  name: string;
  api_url: string;
}

interface TenantPickerProps {
  tenants: TenantInfo[];
  userName?: string;
  onSelect: (tenant: TenantInfo) => void;
}

export default function TenantPicker({ tenants, userName, onSelect }: TenantPickerProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl border border-slate-700/50 p-8 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20 mb-4">
              <Building2 size={28} className="text-white" />
            </div>
            <h1 className="text-xl font-bold text-white mb-1">
              {userName ? `Welcome, ${userName}` : 'Select Tenant'}
            </h1>
            <p className="text-sm text-slate-400">
              Choose which company to access
            </p>
          </div>

          {/* Tenant List */}
          <div className="space-y-3">
            {tenants.map((tenant) => (
              <button
                key={tenant.slug}
                onClick={() => onSelect(tenant)}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-slate-700/50 bg-slate-700/20 hover:bg-slate-700/40 hover:border-slate-600/50 transition-all group text-left"
              >
                <div className="w-11 h-11 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg font-bold text-blue-400">
                    {tenant.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white">{tenant.name}</p>
                  <p className="text-xs text-slate-500 truncate">{tenant.slug}</p>
                </div>
                <ChevronRight size={16} className="text-slate-600 group-hover:text-slate-400 transition-colors flex-shrink-0" />
              </button>
            ))}
          </div>

          {/* Footer */}
          <p className="text-center text-slate-600 text-xs mt-6">
            GeniReports by Genovate Solutions
          </p>
        </div>
      </div>
    </div>
  );
}